import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Route, Routes } from "react-router-dom";
import { ArrowIcon, NotInLocationPicture } from "../../Assets/assets";
import Button from "../../Components/Button/Button";
import CarouselAndAudioPlayer from "../../Components/CarouselAndAudioPlayer/CarouselAndAudioPlayer";
import CropperModal from "../../Components/CropperModal/CropperModal";
import Header from "../../Components/Header/Header";
import InputBox from "../../Components/InputBox/InputBox";
import Modal from "../../Components/Modal/Modal";
import NativeSelect from "../../Components/NativeSelect/NativeSelect";
import TrackProgress from "../../Components/TrackProgress/TrackProgress";
import { useDraftCache } from "../../CustomHooks/useDraftCache";
import {
  completeProcedure,
  getProceduresHistory,
  setSelectedProcedure
} from "../../Redux/procedures/action";
import { isValidArray, isValidObject } from "../../Services/validators";
import {
  CACHE_NAME,
  getAppsUrls,
  locationRange,
  openInNewTab
} from "../../Utils/constants";
import Success from "./Success";

function SelectedProcedure(props) {
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(null);
  const [stepsStatus, setStepsStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [procedureStatus, setProcedureStatus] = useState(null);
  const [formData, setFormData] = useState({
    remarks: "",
    updatedRequiredRemarks: []
  });
  const [file, setFile] = useState({
    image: [],
    audio: null
  });

  const { get, set, update, remove } = useDraftCache(CACHE_NAME);

  useEffect(() => {
    if (stepsStatus) {
      return;
    }
    const selectedProcedure = props.procedures?.selectedProcedure;
    const { procedureId } = selectedProcedure;

    if (procedureId) {
      const procedureSteps = props.procedures?.data?.[procedureId]?.steps;
      if (isValidArray(procedureSteps)) {
        const initializedSteps = procedureSteps.map((step) => ({
          ...step,
          success: null,
          proof: null
        }));
        setStepsStatus(initializedSteps);
      }
    }
    // eslint-disable-next-line
  }, [props.procedures?.selectedProcedure]);

  useEffect(() => {
    const selectedProcedure = props.procedures?.selectedProcedure;
    if (
      selectedProcedure?.procedureId &&
      selectedProcedure?.location?.documentId
    ) {
      getProceduresHistory(
        selectedProcedure?.procedureId,
        selectedProcedure?.location?.documentId
      );
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    async function load() {
      const draft = await loadProcedureDraft(
        get,
        props.locations?.selectedLocation,
        props.procedures?.selectedProcedure?.procedureId
      );
      if (isValidArray(draft)) {
        setStepsStatus(draft);
      }
    }

    load();
    // eslint-disable-next-line
  }, [
    props.locations?.selectedLocation,
    props.procedures?.selectedProcedure?.procedureId
  ]);

  useEffect(() => {
    let _upcomingProcedure = [];
    setLoading(true);
    const selectedProcedure = props.procedures?.selectedProcedure;

    if (!isValidObject(selectedProcedure)) {
      setLoading(false);
      return;
    }

    const {
      period,
      interval,
      procedureId: selectedProcedureId,
      location,
      from,
      to
    } = selectedProcedure;

    const selectedLocationId = location?.documentId;
    const employeeId = props.profile?.data?.employeeId;
    const selectedProcedureData =
      props.procedures.procedureHistory?.[selectedLocationId];
    const offlineProcedureHistoryData =
      props.procedures.offlineProcedureHistory;

    let offlineProcedureHistory = [];
    if (isValidArray(offlineProcedureHistoryData)) {
      const foundHistory = offlineProcedureHistoryData?.find((history) => {
        const procedure = JSON.parse(history.body.procedure);
        const location = JSON.parse(history.body.location);

        return (
          procedure?.id === selectedProcedureId &&
          location?.id === selectedLocationId
        );
      });

      if (foundHistory) {
        const procedure = JSON.parse(foundHistory.body.procedure);
        const location = JSON.parse(foundHistory.body.location);
        const employee = JSON.parse(foundHistory.body.employee);
        const latestUpdate = JSON.parse(foundHistory.body.latestUpdate);
        const createdAt = JSON.parse(foundHistory.body.createdAt);

        offlineProcedureHistory = [
          {
            createdAt,
            latestUpdate,
            procedure,
            location,
            employee
          }
        ];
      }
    }

    let onlineProcedureHistory = [];
    if (isValidObject(selectedProcedureData)) {
      const sortedProcedureHistory = Object.values(selectedProcedureData)
        ?.filter(
          (historyData) =>
            historyData.procedureId === selectedProcedureId &&
            historyData.locationId === selectedLocationId &&
            (!historyData.exclusive || historyData.employeeId === employeeId)
        )
        .sort((first, second) => {
          return second.createdAt - first.createdAt;
        });

      if (sortedProcedureHistory.length > 0) {
        onlineProcedureHistory = [sortedProcedureHistory[0]];
      }
    }

    const history = [
      ...offlineProcedureHistory,
      ...onlineProcedureHistory
    ].sort((first, second) => {
      return second.createdAt - first.createdAt;
    });

    console.log(history, "history");

    if (!from || !to) {
      setLoading(false);
      return;
    }

    const [fromH, fromM] = from?.split(":").map(Number);
    const [toH, toM] = to?.split(":").map(Number);

    if ([fromH, fromM, toH, toM].some(isNaN)) {
      console.error("Invalid time format");
      setLoading(false);
      return;
    }

    const startingTime = new Date().setHours(fromH, fromM, 0, 0);
    const endingTime = new Date().setHours(toH, toM, 0, 0);
    const intervalsInMs = getValidTimingData(period, interval);

    if (!intervalsInMs) {
      // No interval/period assigned — the procedure is on-demand: available
      // any number of times, as long as we're within the FROM-TO window.
      const currentTime = +new Date();
      const withinWindow =
        currentTime >= startingTime && currentTime <= endingTime;
      setLoading(false);
      return setProcedureStatus({
        status: withinWindow,
        timestamp: withinWindow
          ? currentTime
          : currentTime < startingTime
          ? startingTime
          : startingTime + 86400000
      });
    }

    const timestamps = getTimestampsBetween(
      startingTime,
      endingTime,
      intervalsInMs
    );

    const currentTime = +new Date();
    if (isValidArray(history)) {
      if (
        timestamps.find(
          (data) =>
            data > history[0].createdAt &&
            data <= currentTime &&
            data + intervalsInMs >= currentTime
        )
      ) {
        _upcomingProcedure.push(selectedProcedure);
      }
    } else {
      if (
        timestamps.find(
          (data) =>
            data <= currentTime &&
            timestamps[timestamps.length - 1] >= currentTime
        )
      ) {
        _upcomingProcedure.push(selectedProcedure);
      } else {
        if (timestamps.find((data) => data <= currentTime)) {
          _upcomingProcedure.push(selectedProcedure);
        }
      }
    }
    console.log(_upcomingProcedure, "_upcomingProcedure");

    if (_upcomingProcedure.includes(selectedProcedure)) {
      setLoading(false);
      return setProcedureStatus({
        status: true,
        timestamp: timestamps.find((data) => data >= currentTime)
      });
    } else {
      setLoading(false);
      return setProcedureStatus({
        status: false,
        timestamp:
          timestamps.find((data) => data >= currentTime) ||
          timestamps[0] + 86400000
      });
    }
  }, [
    props.procedures?.selectedProcedure,
    props.procedures?.procedureHistory,
    props.procedures?.offlineProcedureHistory,
    props.profile?.data?.employeeId
  ]);

  useEffect(() => {
    if (!props.procedures?.selectedProcedure) {
      props.navigate(-1);
    }
    // eslint-disable-next-line
  }, [props.procedures?.selectedProcedure]);

  const selectedLocationData = useMemo(() => {
    const locationId = props.locations?.selectedLocation;
    return props.locations?.data?.[locationId];
  }, [props.locations?.selectedLocation, props.locations?.data]);

  const selectedProcedureData = useMemo(() => {
    const procedureId = props.procedures.selectedProcedure?.procedureId;
    return props.procedures.data?.[procedureId];
  }, [props.procedures.selectedProcedure?.procedureId, props.procedures.data]);

  useEffect(() => {
    if (procedureStatus?.status === false) {
      props.navigate(
        `/procedures/${props.procedures.selectedProcedure.procedureId}/success`
      );
    }
    // eslint-disable-next-line
  }, [procedureStatus?.status, props.procedures.selectedProcedure.procedureId]);

  function hasValidOfflineProcedureHistory(
    offlineProcedureHistory,
    selectedLocationId,
    selectedProcedureId
  ) {
    return offlineProcedureHistory?.some((history) => {
      const procedure = JSON.parse(history.body.procedure || "{}");
      const location = JSON.parse(history.body.location || "{}");
      return (
        procedure?.id === selectedProcedureId &&
        location?.id === selectedLocationId
      );
    });
  }

  const handleConfirmOnClick = async (success, images, audio, data, i) => {
    const finalAudio = audio || file.audio;

    const proof = [...images, ...(finalAudio ? [finalAudio] : [])].filter(
      Boolean
    );

    let updatedStep = {
      ...stepsStatus[i],
      success,
      remarks: formData?.remarks
    };

    if (data?.requiredRemarks) {
      updatedStep.proof = proof;
      updatedStep.updatedRequiredRemarks = formData?.updatedRequiredRemarks;
    } else if (data?.isProofRequired || success === false) {
      updatedStep.proof = proof;
    }

    const updatedStepsStatus = [...stepsStatus];
    updatedStepsStatus[i] = updatedStep;

    setStepsStatus(updatedStepsStatus);

    await saveProcedureDraft(
      set,
      props.locations?.selectedLocation,
      props.procedures.selectedProcedure?.procedureId,
      updatedStepsStatus
    );

    setSelectedTaskIndex(null);
    setFile({ image: [], audio: null });
  };

  const handleClearOnClick = async (i) => {
    const updatedSteps = [...stepsStatus];

    let clearedStep = {
      ...updatedSteps[i]
    };

    delete clearedStep.proof;
    delete clearedStep.success;
    delete clearedStep.remarks;
    delete clearedStep.updatedRequiredRemarks;

    updatedSteps[i] = clearedStep;

    setStepsStatus(updatedSteps);

    await updateProcedureDraft(
      update,
      props.locations?.selectedLocation,
      props.procedures?.selectedProcedure?.procedureId,
      updatedSteps
    );
  };

  const handleRemoveImage = async (indexToRemove, i) => {
    const step = stepsStatus[i];
    let proofImages = step?.proof ? step?.proof : [];

    if (indexToRemove < 0 || indexToRemove >= proofImages.length) {
      console.warn("Submitted proof removal index out of bounds");
      return;
    }

    proofImages = proofImages.filter((_img, index) => index !== indexToRemove);

    let updatedSteps = structuredClone(stepsStatus);
    updatedSteps[i] = {
      ...step,
      proof: proofImages
    };
    setStepsStatus(updatedSteps);

    await saveProcedureDraft(
      set,
      props.locations?.selectedLocation,
      props.procedures.selectedProcedure?.procedureId,
      updatedSteps
    );
  };

  const handleRemoveAudio = async (i) => {
    const step = stepsStatus[i];
    const proofFiles = isValidArray(step?.proof) ? [...step.proof] : [];

    const audioIndex = proofFiles?.findIndex(
      (file) => file.type === "audio/mp3"
    );

    if (audioIndex === -1) {
      console.warn("No audio file found to remove");
      return;
    }

    proofFiles.splice(audioIndex, 1);

    const updatedSteps = structuredClone(stepsStatus);

    updatedSteps[i] = {
      ...step,
      proof: proofFiles
    };

    setStepsStatus(updatedSteps);

    await saveProcedureDraft(
      set,
      props.locations?.selectedLocation,
      props.procedures.selectedProcedure?.procedureId,
      updatedSteps
    );
  };

  const handleCompleteProcedure = async () => {
    completeProcedure(stepsStatus, props.navigate);
    await deleteProcedureDraft(
      get,
      remove,
      props.locations?.selectedLocation,
      props.procedures?.selectedProcedure?.procedureId
    );
  };

  return (
    <div className="inherit-parent-height inherit-parent-width">
      <Header
        theme={props.auth.theme.primaryColor === "#0E0E0E" ? "dark" : "light"}
        title="Procedure Details"
        backOnClick={() => {
          props.navigate(`/${props.locations?.selectedLocation}/procedures`);
          setSelectedProcedure(null);
        }}
        profileOnClick={() => {
          props.navigate("/settings");
        }}
      />
      <Routes>
        <Route
          path="success"
          element={
            <Success
              employee={props.profile.data}
              isOfflineProcedure={hasValidOfflineProcedureHistory(
                props.procedures.offlineProcedureHistory,
                props.locations?.selectedLocation,
                props.procedures.selectedProcedure?.procedureId
              )}
              location={
                props.locations.selectedLocation &&
                props.locations.data?.[props.locations.selectedLocation]
              }
              procedureStatus={procedureStatus}
              navigate={props.navigate}
            />
          }
        />
      </Routes>

      {isValidObject(props.procedures?.selectedProcedure) && (
        <div className="remaining-body-height display-flex flex-direction-column overflow-hidden">
          {isValidArray(stepsStatus) &&
            procedureStatus?.status === true &&
            props.locations.distance !== null &&
            props.locations.distance <= locationRange && (
              <>
                <div
                  style={{
                    height: "calc(100% - 64px)"
                  }}
                  className="inherit-parent-width max-width-767px"
                >
                  <div className="inherit-parent-width padding-left-large padding-right-large">
                    <Fields
                      title="PROCEDURE"
                      data={selectedProcedureData?.title}
                    />
                    <Fields
                      title="INTERVAL"
                      data={
                        props.procedures?.selectedProcedure?.interval &&
                        props.procedures?.selectedProcedure?.period
                          ? `${props.procedures?.selectedProcedure?.interval} ${props.procedures?.selectedProcedure?.period}`
                          : "On demand"
                      }
                    />
                    <Fields
                      title="LOCATIONS"
                      data={`${selectedLocationData?.PCTNo} - ${selectedLocationData?.location}`}
                    />
                  </div>

                  <div
                    style={{
                      height: "1px",
                      backgroundColor: "var(--tertiaryColor3)"
                    }}
                    className="margin-top-larger margin-left-large margin-right-large"
                  />

                  <div
                    style={{
                      height: "calc(100% - 124px)"
                    }}
                    data-cy="SELECTED-PROCEDURE-TASK-CONTAINER"
                    className="padding-left-large padding-right-large overflow-scroll hide-scrollbar"
                  >
                    <div
                      style={{
                        position: "sticky",
                        top: "0px",
                        backgroundColor: "var(--primaryColor)",
                        zIndex: "101"
                      }}
                      className="padding-bottom-large padding-top-large"
                    >
                      STEPS
                    </div>

                    <div>
                      {(loading || props.procedures.loading) && (
                        <div className="inherit-parent-width">
                          {Array.from({ length: 10 }).map((_, index) => (
                            <div
                              key={index}
                              style={{
                                padding: "32px"
                              }}
                              className="shimmer inherit-parent-width margin-bottom-larger"
                            />
                          ))}
                        </div>
                      )}

                      {!loading &&
                        !props.procedures.loading &&
                        stepsStatus?.map((data, i) => (
                          <React.Fragment key={i}>
                            <TrackProgress
                              isLast={i === stepsStatus.length - 1}
                              title={
                                <div
                                  data-cy={`${data?.title}-card`}
                                  onClick={() => {
                                    if (selectedTaskIndex === i) {
                                      setSelectedTaskIndex(null);
                                    } else {
                                      setSelectedTaskIndex(i);
                                    }
                                  }}
                                  className="inherit-parent-width display-flex flex-justify-content-space-between"
                                >
                                  <div>{data?.title}</div>
                                  {typeof data?.success !== "boolean" && (
                                    <div
                                      style={{
                                        transform: `rotate(${
                                          selectedTaskIndex === i
                                            ? "90deg"
                                            : "0deg"
                                        })`
                                      }}
                                    >
                                      <ArrowIcon color="var(--tertiaryColor6)" />
                                    </div>
                                  )}

                                  {typeof data?.success === "boolean" && (
                                    <div
                                      className="font-size-small"
                                      style={{
                                        color: data.success
                                          ? "var(--secondaryColor)"
                                          : "#D0405A"
                                      }}
                                    >
                                      {data.success ? "PASS" : "FAIL"}
                                    </div>
                                  )}
                                </div>
                              }
                              state={
                                data.success
                                  ? "INACTIVE"
                                  : selectedTaskIndex === i
                                  ? "ACTIVE"
                                  : "PROGRESS"
                              }
                              data={
                                <>
                                  <div
                                    onClick={() => {
                                      if (selectedTaskIndex !== i) {
                                        setSelectedTaskIndex(i);
                                      } else {
                                        setSelectedTaskIndex(null);
                                      }
                                    }}
                                    className="inherit-parent-width"
                                  >
                                    {data?.description}
                                  </div>
                                  {selectedTaskIndex === i && (
                                    <Tasks
                                      onRemoveImage={(indexToRemove) => {
                                        handleRemoveImage(indexToRemove, i);
                                      }}
                                      onRemoveAudio={() => {
                                        handleRemoveAudio(i);
                                      }}
                                      data={data}
                                      formData={formData}
                                      setFormData={setFormData}
                                      add={data?.isProofRequired}
                                      file={file}
                                      setFile={setFile}
                                      confirmClick={(
                                        success,
                                        images,
                                        audio
                                      ) => {
                                        handleConfirmOnClick(
                                          success,
                                          images,
                                          audio,
                                          data,
                                          i
                                        );
                                      }}
                                      clearClick={async () => {
                                        handleClearOnClick(i);
                                      }}
                                    />
                                  )}
                                </>
                              }
                            />
                          </React.Fragment>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="padding-large">
                  <Button
                    type="button"
                    text="COMPLETE"
                    boxShadow={false}
                    loading={props.procedures.loading}
                    disabled={
                      !stepsStatus?.every(
                        (data) => typeof data.success === "boolean"
                      )
                    }
                    onClick={async () => {
                      handleCompleteProcedure();
                    }}
                    data-cy="PROCEDURE-COMPLETE-BUTTON"
                  />
                </div>
              </>
            )}

          {procedureStatus?.status === true &&
            (props.locations.distance === null ||
              props.locations?.distance > locationRange) && (
              <div
                style={{
                  height: "calc(100% - 199px)"
                }}
                className="padding-horizontal-large display-flex flex-direction-column flex-justify-content-center flex-align-items-center"
                data-cy="notInLocation-fallBack"
              >
                <NotInLocationPicture />
                <div className="padding-top-larger font-size-default">
                  Not in location ?
                </div>
                <div className="padding-top-medium text-align-center font-size-medium">
                  Reach the assigned location to continue the procedure.
                </div>
                <div
                  onClick={() => {
                    openInNewTab(
                      `${getAppsUrls.public.url}/${
                        props.selectedProcedure?.location?.documentId
                      }`
                    );
                  }}
                  className="padding-top-medium font-color-primary text-underline"
                >
                  Can't reach ?
                </div>
              </div>
            )}
        </div>
      )}
      <Modal
        show={isValidArray(file?.image)}
        canIgnore={true}
        onClose={() => {
          setFile({ ...file, image: [] });
        }}
        width="inherit-parent-width"
        maxWidth="max-width-800px"
        background="false"
        boxShadow="false"
        borderRadius="false"
        height="inherit-parent-height max-height-500px"
      >
        <div
          data-cy="cropper-modal"
          className={`inherit-parent-height font-color-tertiaryColor6 background-color-tertiaryColor1`}
        >
          <CropperModal
            className="border-radius-default"
            OnBlobCreate={(blob) => {
              setStepsStatus((prev) => {
                const updated = structuredClone(prev);
                const step = updated[selectedTaskIndex];

                const newFile = new File([blob], `${Date.now()}.jpeg`, {
                  type: "image/jpeg"
                });

                step.proof = Array.isArray(step.proof)
                  ? [...step.proof, newFile]
                  : [newFile];

                return updated;
              });
            }}
            file={file.image}
            setFile={() => {
              setFile({ ...file, image: [] });
            }}
          />
        </div>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  profile: state.profile,
  procedures: state.procedures,
  locations: state.locations
});

const mapDispatchToProps = function () {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectedProcedure);

const getValidTimingData = (period, value) => {
  switch (period) {
    case "hours":
      return value * 60 * 60 * 1000;
    case "days":
      return value * 24 * 60 * 60 * 1000;
    case "months":
      return value * 30 * 24 * 60 * 60 * 1000;
    default:
      return 0;
  }
};

function getTimestampsBetween(startingTime, endingTime, interval) {
  const timestamps = [];
  let currentTime = startingTime;
  while (currentTime <= endingTime) {
    timestamps.push(currentTime);
    currentTime += interval;
  }
  return timestamps;
}

export const getMonthlyTimestamps = (
  fromTime,
  numberOfMonths = 12,
  referenceDate = new Date()
) => {
  const [fromH, fromM] = fromTime.split(":").map(Number);

  const safeDay = Math.min(referenceDate.getDate(), 28);

  const timestamps = [];

  for (let i = 0; i < numberOfMonths; i++) {
    const date = new Date(
      referenceDate.getFullYear(),
      referenceDate.getMonth() + i,
      safeDay,
      fromH,
      fromM,
      0,
      0
    );
    timestamps.push(+date);
  }

  return timestamps;
};

export const Fields = (props) => {
  return (
    <div className="display-flex font-size-small padding-top-large">
      <div className="display-flex">
        <div
          className="white-space-no-wrap  font-color-tertiaryColor6"
          style={{ width: "150px" }}
          data-cy={`${props.title}`}
        >
          {props.title}
        </div>
      </div>
      <div
        className={`text-align-left inherit-parent-width text-overflow-ellipsis font-color-tertiaryColor ${
          !props.removeCapitalize && "text-capitalize"
        }`}
        data-cy={props.dataCy || props.data}
        style={{
          textWrap: "wrap"
        }}
      >
        {props.data}
      </div>
    </div>
  );
};

const Tasks = (props) => {
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const [success, setSuccess] = useState(null);
  const [visibleMedia, setVisibleMedia] = useState({ images: [], audio: null });
  const [isAudioRecording, setIsAudioRecording] = useState(false);

  useEffect(() => {
    if (typeof props.data.success === "boolean") {
      setSuccess(props.data.success);
    }
  }, [props.data.success]);

  useEffect(() => {
    const proof = props.data?.proof ? props.data?.proof : [];
    const images = proof
      .filter(
        (file) => file && file.type !== "audio/mp3" && file instanceof Blob
      )
      .map((file) => URL.createObjectURL(file));

    const audio =
      proof.find((file) => file && file.type === "audio/mp3") || null;

    setVisibleMedia({
      images,
      audio
    });
  }, [props.data?.proof]);

  const getFile = useMemo(() => {
    const proofImages =
      props.data?.proof?.filter((file) => file && file.type !== "audio/mp3") ||
      [];

    const audioFile = props.data?.proof?.find(
      (file) => file && file.type === "audio/mp3"
    );

    return {
      images: proofImages,
      ...(audioFile ? { audio: audioFile || null } : {})
    };
  }, [props.data?.proof]);

  useEffect(() => {
    const isProofRequired = props.data?.isProofRequired;

    const hasProof =
      Array.isArray(props.data?.proof) && props.data.proof.length > 0;

    const remarksPresent =
      props.data?.remarks?.trim()?.length > 0 ||
      props.formData?.remarks?.trim()?.length > 0;

    const hasRequiredRemarks =
      Array.isArray(props.data?.requiredRemarks) &&
      props.data.requiredRemarks.length > 0;

    if (isAudioRecording) {
      setIsConfirmDisabled(true);
      return;
    }

    if (isProofRequired || success === false) {
      if (!hasProof && !remarksPresent) {
        setIsConfirmDisabled(true);
        return;
      }
    }

    if (hasRequiredRemarks) {
      if (
        props.data.requiredRemarks.length !==
          props.formData.updatedRequiredRemarks?.filter(Boolean)?.length ||
        !props.formData.updatedRequiredRemarks?.every(
          (remarks) => remarks.value?.trim().length > 0
        )
      ) {
        setIsConfirmDisabled(true);
        return;
      }
    }

    setIsConfirmDisabled(false);

    // eslint-disable-next-line
  }, [props.data, props.formData, success, isAudioRecording]);

  useEffect(() => {
    const hasRemarks = typeof props.data?.remarks === "string";
    const hasUpdatedRequiredRemarks = Array.isArray(
      props.data?.updatedRequiredRemarks
    );

    props.setFormData((prev) => ({
      ...prev,
      remarks: hasRemarks ? props.data.remarks : "",
      updatedRequiredRemarks: hasUpdatedRequiredRemarks
        ? props.data.updatedRequiredRemarks
        : []
    }));

    // eslint-disable-next-line
  }, [props.data?.remarks, props.data.updatedRequiredRemarks]);

  const getOptions = useMemo(() => {
    return (i) => {
      if (!isValidArray(props.data.requiredRemarks)) {
        return [];
      }

      return props.data?.requiredRemarks[i]?.options?.map((data) => ({
        title: data.value,
        key: data.value,
        disabled: false
      }));
    };
  }, [props.data.requiredRemarks]);

  return (
    <div>
      {(typeof success !== "boolean" ||
        (success === true && props.data.isProofRequired === false)) && (
        <div className="display-flex">
          <div
            style={{
              width: "100%"
            }}
            className="padding-right-default"
          >
            <Button
              type="button"
              className="margin-top-larger"
              text="YES"
              boxShadow={false}
              loading={props.loading}
              onClick={() => {
                setSuccess(true);
                if (!props.data?.isProofRequired) {
                  props.confirmClick(true, [], null);
                }
              }}
              data-cy={`${props.data.title}-pass-button`}
            />
          </div>
          <div
            style={{
              width: "100%"
            }}
            className="padding-left-default"
          >
            <Button
              type="button"
              variant="transparent"
              className="margin-top-larger"
              text="NO"
              boxShadow={false}
              onClick={() => {
                setSuccess(false);
              }}
              data-cy={`${props.data.title}-fail-button`}
            />
          </div>
        </div>
      )}

      {(success === false ||
        (success === true && props.data.isProofRequired === true)) && (
        <div className="inherit-parent-width padding-top-default">
          <div
            className={` ${
              visibleMedia.images.length > 0
                ? "margin-bottom-large"
                : "margin-bottom-large"
            }`}
          >
            <InputBox
              className={`inherit-parent-width margin-top-large`}
              name="name"
              label="REMARKS (OPTIONAL)"
              type="text"
              onChange={(e) => {
                props.setFormData({
                  ...props.formData,
                  remarks: e.target.value
                });
              }}
              placeholder="Enter remarks"
              removeResponsive={true}
              value={props.formData?.remarks}
              fontColor="font-color-tertiaryColor"
              labelFontColor="font-color-tertiaryColor"
              data-cy="REMARKS-FIELD"
              disabledBackgroundColor="transparent"
              required
            />
          </div>
          {isValidArray(props.data.requiredRemarks) &&
            props.data.requiredRemarks.map((remarks, i) => (
              <div key={i} className="inherit-parent-width padding-top-large">
                {isValidArray(remarks.options) && (
                  <NativeSelect
                    name={remarks.title}
                    removeResponsive={true}
                    label={remarks.title}
                    selectClassName="text-transform-capitalize"
                    options={[" ", ...getOptions(i).map((d) => d.title)]}
                    defaultValue={
                      props.formData.updatedRequiredRemarks?.[i]?.value
                    }
                    onChange={(event) => {
                      const kpiValue =
                        event.target.value.trim().length > 0 &&
                        remarks.options.find(
                          (option) => option.value === event.target.value
                        )?.kpi;

                      props.setFormData((prevData) => {
                        let requiredRemarks = prevData.updatedRequiredRemarks;
                        requiredRemarks[i] = {
                          title: remarks.title,
                          ...(isValidObject(kpiValue)
                            ? {
                                kpi: kpiValue
                              }
                            : {}),
                          value: event.target.value
                        };

                        return {
                          ...prevData,
                          updatedRequiredRemarks: requiredRemarks
                        };
                      });
                    }}
                    fontColor={
                      props.theme === "dark"
                        ? "font-color-white"
                        : "font-color-secondary"
                    }
                  />
                )}
                {remarks.dataType && (
                  <InputBox
                    className="inherit-parent-width"
                    name="name"
                    label={remarks.title}
                    type={remarks.dataType === "free" ? "text" : "number"}
                    onChange={(event) => {
                      props.setFormData((prevData) => {
                        let requiredRemarks = prevData.updatedRequiredRemarks;
                        requiredRemarks[i] = {
                          title: remarks.title,
                          value: event.target.value
                        };

                        return {
                          ...prevData,
                          updatedRequiredRemarks: requiredRemarks
                        };
                      });
                    }}
                    placeholder="Enter corresponding values"
                    removeResponsive={true}
                    value={props.formData.updatedRequiredRemarks?.[i]?.value}
                    fontColor="font-color-tertiaryColor"
                    labelFontColor="font-color-tertiaryColor"
                    data-cy="UPDATE-FIELD"
                    disabledBackgroundColor="transparent"
                    required
                  />
                )}
              </div>
            ))}
          {props.add && (
            <div
              style={{
                paddingBottom: props.data?.proof?.length > 0 ? "16px" : "0px"
              }}
            >
              UPLOAD FILES OR ADD VOICE NOTE
            </div>
          )}
          <CarouselAndAudioPlayer
            // dataCy={data?.title}
            isAudioRecoding={setIsAudioRecording}
            onRemoveImage={props.onRemoveImage}
            onRemoveAudio={props.onRemoveAudio}
            assetsLoading={props.complaintsLoading?.assets}
            fileSrc={{
              images: visibleMedia.images?.filter(Boolean),
              ...(visibleMedia.audio ? { audio: visibleMedia.audio } : {})
            }}
            dataCy="update-section"
            setFile={props.setFile}
            file={props.file}
            proofType="live"
            add={props.add || success === false}
            setErrorStatus={props.setErrorStatus}
            onImageClick={(data) => {
              if (typeof props.setPreviewImage === "function") {
                props.setPreviewImage(data);
              }
            }}
          />
          <div className="display-flex inherit-parent-width">
            <div className="padding-right-default  inherit-parent-width">
              <Button
                type="button"
                className="margin-top-larger"
                text="CONFIRM"
                boxShadow={false}
                loading={props.loading}
                onClick={() => {
                  props.setFormData({
                    remarks: ""
                  });
                  props.confirmClick(success, getFile.images, getFile.audio);
                }}
                disabled={isConfirmDisabled}
                data-cy="PROCEDURE-STEP-CONFIRM-BUTTON"
              />
            </div>
            <div className="padding-left-default  inherit-parent-width">
              <Button
                type="button"
                variant="transparent"
                className="margin-top-larger"
                text="CLEAR"
                boxShadow={false}
                onClick={() => {
                  setSuccess("");
                  props.clearClick();
                }}
                data-cy="PROCEDURE-STEP-CLEAR-BUTTON"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export async function saveProcedureDraft(
  set,
  locationId,
  procedureId,
  stepsStatus
) {
  const key = `/draft/${locationId}_${procedureId}/metadata`;

  const metadataSteps = stepsStatus.map((step) => ({
    ...step,
    proof: Array.isArray(step.proof)
      ? step.proof.map((file) => {
          const name = file.name || "";
          const extension = name.includes(".") ? name.split(".").pop() : "";

          return {
            name,
            type: file.type || "",
            extension
          };
        })
      : []
  }));

  const metadata = {
    latestUpdate: metadataSteps,
    stepCount: metadataSteps.length
  };

  const fileMap = {};
  stepsStatus.forEach((step, stepIdx) => {
    if (Array.isArray(step.proof)) {
      step.proof.forEach((file, fileIdx) => {
        const name = file.name || "";
        const extension = name.includes(".") ? name.split(".").pop() : "";
        if (!extension) return;

        const fileKey = `/draft/${locationId}_${procedureId}/step/${stepIdx}/proof/${fileIdx}.${extension}`;
        fileMap[fileKey] = file;
      });
    }
  });

  return set(key, metadata, fileMap);
}

export async function loadProcedureDraft(get, locationId, procedureId) {
  if (!locationId || !procedureId) {
    return [];
  }

  const metadataKey = `/draft/${locationId}_${procedureId}/metadata`;

  const metaResp = await get(metadataKey);
  if (!metaResp?.data) {
    return [];
  }

  const steps = metaResp.data.latestUpdate;

  const fileKeys = [];
  steps.forEach((step, stepIdx) => {
    if (isValidArray(step.proof)) {
      step.proof.forEach((proofMeta, fileIdx) => {
        if (!proofMeta.extension) {
          return;
        }
        fileKeys.push(
          `/draft/${locationId}_${procedureId}/step/${stepIdx}/proof/${fileIdx}.${proofMeta.extension}`
        );
      });
    }
  });

  const { data, files } = await get(metadataKey, fileKeys);

  return data.latestUpdate.map((step, stepIdx) => {
    const proof = [];
    if (isValidArray(step.proof)) {
      step.proof.forEach((proofMeta, fileIdx) => {
        if (!proofMeta.extension) {
          return;
        }
        const fileKey = `/draft/${locationId}_${procedureId}/step/${stepIdx}/proof/${fileIdx}.${proofMeta.extension}`;
        if (files[fileKey]) {
          proof.push(files[fileKey]);
        }
      });
    }

    return { ...step, proof };
  });
}

export async function deleteProcedureDraft(
  get,
  remove,
  locationId,
  procedureId
) {
  if (!locationId || !procedureId) {
    return;
  }

  const key = `/draft/${locationId}_${procedureId}/metadata`;

  const metaResp = await get(key);
  if (!metaResp?.data) {
    return;
  }

  const steps = metaResp.data.latestUpdate || [];

  const deleteKeys = [
    key,
    ...steps.flatMap((step, stepIdx) =>
      (step.proof || [])
        .map((proofMeta, proofIdx) => {
          if (!proofMeta.extension) return null;
          return `/draft/${locationId}_${procedureId}/step/${stepIdx}/proof/${proofIdx}.${proofMeta.extension}`;
        })
        .filter(Boolean)
    )
  ];

  await remove(deleteKeys);
}

export async function updateProcedureDraft(
  update,
  locationId,
  procedureId,
  stepsStatus
) {
  if (!locationId || !procedureId) {
    return;
  }

  const key = `/draft/${locationId}_${procedureId}/metadata`;

  await update(key, (oldData) => ({
    ...oldData,
    latestUpdate: stepsStatus
  }));
}
